// What waits to be applied and what is being applied, in one control at the
// top right of the shell (#2336, at Gabriel Provencher's request of
// 2026-11-02). It took the place of *Changes to apply* and *Applies*, which
// sat side by side. Each half keeps the rules ZENITH.md §18 gives it, and
// nothing here reads anything the two did not:
//
// - **The changes half** (`ChangesToApply.tsx`, #1811, #1826, #2244): only at a
//   site and only for a caller holding `network.write` there; nothing for an
//   answered empty list or one of plans not made; *not read*, *not derived* and
//   *not asked* each drawn; nothing before the first answer; the last answer kept
//   through a re-read; the approval being followed, whatever the list says.
// - **The applies half** (`AppliesFollowed.tsx`, ADR-0480 decision 4a, ADR-0536
//   decisions 1a and 4a): on every page, each apply this tab sent or a new tab
//   found, until dismissed.
//
// So a page with neither draws nothing, a page outside a site shows only the
// applies, and a caller without `network.write` only theirs. The top bar shows
// each half's words side by side; the panel under it holds the way to Design ›
// Changes, the approval being followed and the applies.
//
// **The reads are the halves' own and are held where they were.** The applies
// half is read by this component, which the shell mounts once and never
// remounts, so a new tab's read of the open applies is asked once and the change
// stream is held only while an apply is open. The changes half is read by a
// component keyed by the site, as it was, so a new site starts with no last
// answer and no approval followed from the last one.
import type { ReactNode } from "react";
import { AppliesSection, useApplies, type Applies } from "./AppliesFollowed";
import { ChangesSection, ChangesWord, FollowingSection, useChanges, useChangesSite, type Changes } from "./ChangesToApply";

export default function ChangesAndApplies() {
  const applies = useApplies();
  const siteId = useChangesSite();
  return siteId
    ? <AtSite key={siteId} siteId={siteId} applies={applies} />
    : <Control applies={applies} />;
}

function AtSite({ siteId, applies }: { siteId: string; applies: Applies }) {
  return <Control changes={useChanges(siteId)} applies={applies} />;
}

function Control({ changes, applies }: { changes?: Changes; applies: Applies }) {
  const words = changes?.words;
  const following = changes?.following;
  if (!words && !following && applies.list.length === 0) return null;
  const parts: ReactNode[] = [];
  if (words) parts.push(<ChangesWord key="changes" words={words} />);
  if (following) parts.push(<span key="following" className="shell-activity-word">{following.summary}</span>);
  if (applies.summary) parts.push(<span key="applies" className="shell-activity-word">{applies.summary}</span>);
  return (
    <details className="shell-activity" data-open-applies={applies.running}>
      <summary>{parts.flatMap((p, i) => i === 0 ? [p] : [<span key={`sep${i}`} className="muted" aria-hidden="true"> · </span>, p])}</summary>
      <div className="shell-following-panel">
        {words && <ChangesSection words={words} />}
        {following && changes && <FollowingSection siteId={changes.siteId} following={following} />}
        {applies.list.length > 0 && <AppliesSection applies={applies} />}
      </div>
    </details>
  );
}
